import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, Lock, Eye, EyeOff, CheckCircle2, ArrowLeft, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Geçersiz veya eksik token. Lütfen email linkini tekrar kontrol edin.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Şifreniz başarıyla güncellendi');
    } catch (error) {
      const message = error.response?.data?.detail || 'Bir hata oluştu';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md shadow-2xl border-none">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Yeni Şifre Belirle</CardTitle>
          <CardDescription>
            {success 
              ? 'Şifreniz başarıyla güncellendi'
              : 'Yeni şifrenizi girin'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && !success && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-700">{error}</p>
                {!token && (
                  <Button 
                    variant="link" 
                    className="text-red-600 p-0 h-auto mt-2"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Yeni sıfırlama linki al →
                  </Button>
                )}
              </div>
            </div>
          )}

          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Şifre Güncellendi!</h3>
              <p className="text-slate-600 text-sm mb-6">
                Yeni şifrenizle giriş yapabilirsiniz.
              </p>
              <Button 
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600"
                onClick={() => navigate('/login')}
              >
                Giriş Yap
              </Button>
            </div>
          ) : token ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Yeni Şifre</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Şifre Tekrar</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 py-6"
                disabled={loading}
              >
                {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
              </Button>
              
              <div className="text-center pt-4">
                <button 
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-slate-600 hover:text-emerald-600"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Giriş sayfasına dön
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate('/forgot-password')}
              >
                Şifre Sıfırlama İste
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
